import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { AutoDialerMapping, AutomationType, TrellusDisposition } from "interfaces/settings";
import { useUserData } from '../../../lib/redux/users/user-data-hook';
import {
    updateMappingByPlatform,
    resetAdjustedMappings,
    clearError,
    loadAutodialerMappings,
    setSaving,
    saveMappingsSuccess,
    saveMappingsFailure,
} from 'lib/redux/settings/autodialerMappingsSlice';
import { SelectionType, UserSelection } from "interfaces/settings";
import { getServicesManager } from 'services';

export function useAutodialerMappings() {
    const dispatch = useDispatch();
    
    const {
        savedMappings,
        adjustedMappings,
        mappingOptions,
        isLoading,
        isSaving,
        error,
        hasUnsavedChanges,
    } = useSelector((state: RootState) => state.autodialerMappings);
    
    // Import platform and selection state from settings
    const { selectedPlatform, selection } = useSelector((state: RootState) => state.autodialerSettings);
    
    const { user, isAdmin } = useUserData();
    const userGroups = useSelector((state: RootState) => state.userGroupInfo.sortedGroupInfo);

    // Helper functions that match the settings hook logic exactly
    const mappingsForPlatform = useMemo(() => {
        if (!selectedPlatform) return [];
        return adjustedMappings.filter(m => m.platform === selectedPlatform);
    }, [selectedPlatform, adjustedMappings]);

    const getNonNullMappingsForPlatform = useCallback((automationType: AutomationType, disposition: TrellusDisposition) => {
        return mappingsForPlatform.filter(m => 
            m.automation_type === automationType && 
            m.trellus_disposition === disposition &&
            m.value !== null && 
            m.value !== undefined &&
            m.value !== ''
        );
    }, [mappingsForPlatform]);

    const getPriorityMappingForSelection = (mappings: AutoDialerMapping[]): AutoDialerMapping => {
        return mappings.sort((a, b) => {
            if (a.locked_user_id !== "") return -1;
            if (b.locked_user_id !== "") return 1;
            if (a.user_group_id !== "") return -1;
            if (b.user_group_id !== "") return 1;
            if (a.team_id !== "") return -1;
            if (b.team_id !== "") return 1;
            return 0;
        })[0];
    };

    const getMappingForSelection = useCallback((
        automationType: AutomationType,
        disposition: TrellusDisposition
    ): AutoDialerMapping | null => {
        if (!selectedPlatform || !selection) return null;
        
        const nonNullMappings = getNonNullMappingsForPlatform(automationType, disposition)
        
        // Filter eligible mappings based on selection type (same logic as settings)
        const eligibleMappings = nonNullMappings.filter(m => {
            switch (selection.type) {
                case SelectionType.ME:
                case SelectionType.USER:
                    return m.user_id === selection.id || 
                           m.locked_user_id === selection.id ||    
                           (m.user_group_id !== '' && userGroups.find((v) => v.allUserIds.includes(selection.id) && v.group.user_group_id === m.user_group_id) !== undefined) ||
                           m.team_id === user?.team_id;
                case SelectionType.OVERALL_TEAM:
                    return m.team_id === selection.id;
                case SelectionType.SUB_TEAM:
                    return m.user_group_id === selection.id || m.team_id !== '';
            }
            return false;
        });
        if (eligibleMappings.length === 0) return null;
        const priorityMapping = getPriorityMappingForSelection(eligibleMappings);
        return priorityMapping;
    }, [selectedPlatform, selection, getNonNullMappingsForPlatform, userGroups, user]);

    const getValueForMapping = useCallback((
        automationType: AutomationType,
        disposition: TrellusDisposition
    ): string | null => {
        const mapping = getMappingForSelection(automationType, disposition);
        return mapping ? mapping.value : null;
    }, [getMappingForSelection]);

    const getMappingSetBySelection = useCallback((
        automationType: AutomationType,
        disposition: TrellusDisposition
    ): UserSelection | null => {
        const mapping = getMappingForSelection(automationType, disposition);
        if (!mapping) return null;

        if (mapping.locked_user_id !== "") return { type: SelectionType.USER, id: mapping.locked_user_id };
        if (mapping.user_group_id !== "") return { type: SelectionType.SUB_TEAM, id: mapping.user_group_id };
        if (mapping.team_id !== "") return { type: SelectionType.OVERALL_TEAM, id: mapping.team_id };
        return { type: SelectionType.ME, id: mapping.user_id };
    }, [getMappingForSelection]);

    const getMappingIsEditable = useCallback((
        automationType: AutomationType,
        disposition: TrellusDisposition
    ): boolean => {
        if (isAdmin) return true;
        const mappingSet = getMappingSetBySelection(automationType, disposition);
        return !mappingSet || mappingSet.type === SelectionType.ME;
    }, [getMappingSetBySelection, isAdmin]);

    const getMappingSetByDifferentSelection = useCallback((
        automationType: AutomationType,
        disposition: TrellusDisposition
    ): boolean => {
        if (!selectedPlatform || !selection) return false;
        const mappingSet = getMappingSetBySelection(automationType, disposition);
        if (mappingSet && selection.type === SelectionType.ME && isAdmin && mappingSet.type === SelectionType.USER && mappingSet.id === selection.id) return false;
        return mappingSet !== null && mappingSet.type !== selection.type;
    }, [getMappingSetBySelection, selectedPlatform, selection, isAdmin]);

    const updateMappingForSelection = useCallback((
        automationType: AutomationType,
        disposition: TrellusDisposition,
        valueSet: string | null
    ) => {
        if (!selectedPlatform || !selection) return;
        let selectionToUse = selection;
        if (selection.type === SelectionType.ME) {
            const canEditCurrentMapping = getMappingIsEditable(automationType, disposition);
            if (!canEditCurrentMapping) return
            // if it's currently set by a different selection, we need to use the locked user id
            const mappingSet = getMappingSetBySelection(automationType, disposition);
            if (mappingSet && mappingSet.type !== SelectionType.ME) { selectionToUse = { type: SelectionType.USER, id: selection.id }; }
        }
        const value = valueSet;
        const mappingUpdate: Partial<AutoDialerMapping> = {
            automation_type: automationType,
            platform: selectedPlatform,
            trellus_disposition: disposition,
            value: value ?? undefined,
        }
        dispatch(updateMappingByPlatform({
            platform: selectedPlatform,
            mapping: mappingUpdate,
            selection: selectionToUse,
        }));
    }, [dispatch, selectedPlatform, selection, getMappingIsEditable, getMappingSetBySelection]);

    const loadMappings = useCallback(() => {
        return loadAutodialerMappings(dispatch);
    }, [dispatch])

    const getUserSelectionForMapping = useCallback((mapping: AutoDialerMapping) => {
        if (mapping.locked_user_id !== "") return { type: SelectionType.USER, id: mapping.locked_user_id };
        if (mapping.user_group_id !== "") return { type: SelectionType.SUB_TEAM, id: mapping.user_group_id };
        if (mapping.team_id !== "") return { type: SelectionType.OVERALL_TEAM, id: mapping.team_id };
        return { type: SelectionType.ME, id: mapping.user_id };
    }, [])

    const mappingsAreEqual = useCallback((mapping1: AutoDialerMapping, mapping2: AutoDialerMapping) => {
        return JSON.stringify(getUserSelectionForMapping(mapping1)) === JSON.stringify(getUserSelectionForMapping(mapping2)) &&
                mapping1.automation_type === mapping2.automation_type &&
                mapping1.platform === mapping2.platform &&
                mapping1.trellus_disposition === mapping2.trellus_disposition
    }, [getUserSelectionForMapping])

    const saveMappings = useCallback(() => {
        dispatch(setSaving(true));
        const saveMappingPromises: Promise<boolean>[] = [];
        const removedMappings = savedMappings.filter((savedMapping: AutoDialerMapping) => {
            return !adjustedMappings.find((adjustedMapping: AutoDialerMapping) => mappingsAreEqual(savedMapping, adjustedMapping));
        })
    
        removedMappings.forEach((mapping: AutoDialerMapping) => {
            const userSelection = getUserSelectionForMapping(mapping);
            const promise = getServicesManager().putAutodialerMappings(
                mapping,
                userSelection,
                true
            ).then(success => success === true);
            saveMappingPromises.push(promise);
        });
        adjustedMappings.forEach((adjustedMapping: AutoDialerMapping) => {
            const savedMapping = savedMappings.find((saved: AutoDialerMapping) => mappingsAreEqual(saved, adjustedMapping));
            let needsSaving = false;
            if (!savedMapping) needsSaving = true;
            else if (savedMapping.value !== adjustedMapping.value) needsSaving = true;
            if (needsSaving) {
                const userSelection = getUserSelectionForMapping(adjustedMapping);
                const isRemove = !adjustedMapping.value || adjustedMapping.value === null || adjustedMapping.value === '';
                const promise = getServicesManager().putAutodialerMappings(adjustedMapping, userSelection, isRemove).then(success => success === true);
                saveMappingPromises.push(promise);
            }
        });
    
        return Promise.all(saveMappingPromises)
            .then(results => {
                const allSuccessful = results.every(result => result === true);
                if (allSuccessful) dispatch(saveMappingsSuccess());
                else dispatch(saveMappingsFailure('Some mappings failed to save'));
            })
            .catch(error => {
                dispatch(saveMappingsFailure(error.message || 'Failed to save mappings'));
            }).finally(() => dispatch(setSaving(false)))
    }, [dispatch, savedMappings, adjustedMappings, mappingsAreEqual, getUserSelectionForMapping])

    const resetMappings = useCallback(() => {
        dispatch(resetAdjustedMappings());
    }, [dispatch]);

    const dismissError = useCallback(() => {
        dispatch(clearError());
    }, [dispatch]);

    const getOptionsForSelection = useCallback((
        automationType: AutomationType
    ): string[] => {
        if (!selectedPlatform) return [];
        const platformOptions = mappingOptions.find(option => 
            option.platform === selectedPlatform && 
            option.automation_type === automationType
        );
        return platformOptions ? platformOptions.options : [];
    }, [mappingOptions, selectedPlatform]);

    return {
        // State
        savedMappings,
        adjustedMappings,
        mappingOptions,
        isLoading,
        isSaving,
        error,
        hasUnsavedChanges,
        selectedPlatform,
        selection,
        
        // Primary actions (matching settings hook)
        updateMappingForSelection,
        loadMappings,
        saveMappings,
        resetMappings,
        dismissError,
        
        // Helper functions for current selection (matching settings hook)
        getMappingIsEditable,
        getMappingForSelection,
        getValueForMapping,
        getMappingSetByDifferentSelection,
        getMappingSetBySelection,
        getOptionsForSelection,
    };
} 